import { Add, Close } from '@mui/icons-material';
import { Box, Button, Dialog, Grid, Typography, useTheme } from '@mui/material';
import { Loading } from '@/components/atoms/Loading';
import { ProfileForm } from '@/components/forms/ProfileForm';
import { UserForm } from '@/components/forms/UserForm';
import { MainCard } from '@/components/molecules/MainCard';
import { PartnerAccordion } from '@/components/molecules/PartnerAccordion';
import { UserRow } from '@/components/molecules/UserRow';
import { useCreateNonPartnerUserMutation } from '@/hooks/useCreateNonPartnerUserMutation';
import { useCreatePartnerMutation } from '@/hooks/useCreatePartnerMutation';
import { useCreateUserFromPartnerMutation } from '@/hooks/useCreateUserFromPartnerMutation';
import { usePartnersQuery } from '@/hooks/usePartnersQuery';
import { useSnackbar } from 'notistack';
import type { AuthUser, Partner } from '@/openapi';
import { UsersService } from '@/openapi';
import { useAuth } from '@/providers/AuthProvider';
import React, { useMemo } from 'react';
import { useModal } from 'react-modal-hook';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/util/queryKeys';

const employeeDefinitions = [
  { title: 'Partners', status: 'partner' },
  { title: 'Employees', status: 'employee' },
  { title: 'Mid-Level', status: 'midlevel' },
  { title: 'Retired', status: 'retired' },
];

export const Partners: React.FC = () => {
  const { isLoading: isLoadingPartners, data: partners } = usePartnersQuery();

  const { isLoading: isLoadingUsers, data: users } = useQuery({
    queryKey: queryKeys.users.list(),
    queryFn: () => UsersService.usersServiceGetUsers(),
  });

  const { unifiedPartners, unmatchedUsers, unmatchedPartners } = useMemo(() => {
    if (!partners || !users) {
      return {
        unifiedPartners: [],
        unmatchedUsers: [],
        unmatchedPartners: [],
      };
    }
    const unifiedPartners = partners.data.filter((partner) =>
      users.data.find((user) => user.uid === partner.account_id)
    );
    const unifiedPartnerIds = unifiedPartners.map((p) => p.account_id!);
    const unmatchedPartners = partners.data.filter(
      (partner) =>
        partner.account_id && !unifiedPartnerIds.includes(partner.account_id)
    );
    const unmatchedUsers = users.data.filter(
      (user) => !unifiedPartnerIds.includes(user.uid)
    );
    return {
      unifiedPartners,
      unmatchedUsers,
      unmatchedPartners,
    };
  }, [partners, users]);
  const theme = useTheme();
  const snackbar = useSnackbar();
  const { isAdmin } = useAuth();

  const defaultPartner: Partner = {
    id: 0,
    account_id: '',
    firstName: '',
    middleName: '',
    lastName: '',
    initials: '',
    birthday: '',
    email: '',
    fax: '',
    pager: '',
    status: '',
    spouseName: '',
    hireDate: '',
    endDate: '',
    partnerDate: '',
    retirementDate: '',
    line1: '',
    line2: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    mobile: '',
    jobTitle: '',
    shiftPreferences: undefined,
  };
  const defaultUser: AuthUser = {
    uid: '',
    displayName: '',
    email: '',
    role: '',
  };

  const createPartnerMutation = useCreatePartnerMutation();
  const createUserFromPartnerMutation = useCreateUserFromPartnerMutation();
  const createPartnerAndUser = async (partner: Partner) => {
    try {
      const newPartner = await createPartnerMutation.mutateAsync(partner);
      await createUserFromPartnerMutation.mutateAsync(newPartner.data.id);
      snackbar.enqueueSnackbar('Partner and user created successfully', {
        variant: 'success',
      });
    } catch {
      snackbar.enqueueSnackbar('Failed to create partner and user', {
        variant: 'error',
      });
    }
  };

  const [showModal, hideModal] = useModal(() => (
    <Dialog open={true} fullWidth maxWidth="lg">
      <Grid
        container
        sx={{
          justifyContent: 'right',
          width: '100%',
        }}>
        <Button onClick={hideModal}>
          <Close />
        </Button>
      </Grid>
      <Typography
        variant="h2"
        sx={{
          textAlign: 'center',
        }}>
        Create New Partner
      </Typography>
      <Box
        sx={{
          py: 4,
          minWidth: theme.spacing(90),
          maxWidth: theme.spacing(120),
        }}>
        <ProfileForm
          mode="modal"
          partner={defaultPartner}
          partnerUser={defaultUser}
          onSubmit={async (partner) => {
            await createPartnerAndUser(partner);
            hideModal();
          }}
          onCancel={hideModal}
        />
      </Box>
    </Dialog>
  ));

  const PartnersCard: React.FC<{ partners: Array<Partner> }> = ({
    partners,
  }) => {
    return (
      <MainCard>
        <Grid
          container
          sx={{
            alignItems: 'baseline',
            pl: 2,
            pr: 5,
            mb: 2,
            width: '100%',
          }}>
          <Grid
            size={{
              xs: 8,
              md: 3,
            }}>
            <Typography variant="h4">Name</Typography>
          </Grid>
          <Grid
            size={{
              xs: 4,
              md: 2,
            }}>
            <Typography variant="h4">Initials</Typography>
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 3,
            }}>
            <Typography variant="h4">Mobile</Typography>
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 3,
            }}>
            <Typography variant="h4">Email</Typography>
          </Grid>
          {isAdmin && (
            <Grid
              size={{
                xs: 12,
                md: 1,
              }}>
              <Button variant="contained" size="small" onClick={showModal}>
                <Add />
                New
              </Button>
            </Grid>
          )}
        </Grid>
        {partners.length > 0
          ? partners
              .sort((a, b) => a.initials?.localeCompare(b.initials ?? '') ?? 0)
              .map((partner) => {
                const user = users!.data.find(
                  (user) => user.uid === partner.account_id
                )!;
                return (
                  <PartnerAccordion
                    key={partner.id}
                    partner={partner}
                    user={user}
                  />
                );
              })
          : 'No partners found.'}
      </MainCard>
    );
  };

  const createNonPartnerUserMutation = useCreateNonPartnerUserMutation();
  const createNonPartnerUser = async (user: AuthUser) => {
    try {
      await createNonPartnerUserMutation.mutateAsync(user);
      snackbar.enqueueSnackbar('User created successfully', {
        variant: 'success',
      });
    } catch {
      snackbar.enqueueSnackbar('Failed to create shift', {
        variant: 'error',
      });
    }
  };

  const [showUserModal, hideUserModal] = useModal(() => (
    <Dialog open={true} fullWidth maxWidth="sm">
      <Grid
        container
        sx={{
          justifyContent: 'right',
          width: '100%',
        }}>
        <Button onClick={hideUserModal}>
          <Close />
        </Button>
      </Grid>
      <Typography
        variant="h2"
        sx={{
          textAlign: 'center',
        }}>
        Create New User
      </Typography>
      <Box
        sx={{
          py: 4,
          minWidth: theme.spacing(50),
          maxWidth: theme.spacing(70),
        }}>
        <UserForm
          mode="modal"
          user={defaultUser}
          onSubmit={async (authUser) => {
            await createNonPartnerUser(authUser);
            hideUserModal();
          }}
          onCancel={hideUserModal}
        />
      </Box>
    </Dialog>
  ));

  const UsersCard: React.FC<{ users: Array<AuthUser> }> = ({ users }) => {
    return (
      <MainCard>
        <Grid
          container
          sx={{
            pl: 2,
            pr: 5,
            mb: 2,
            width: '100%',
          }}>
          <Grid
            size={{
              xs: 12,
              md: 4,
            }}>
            <Typography variant="h4">Name</Typography>
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 4,
            }}>
            <Typography variant="h4">Email</Typography>
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 3,
            }}>
            <Typography variant="h4">Access</Typography>
          </Grid>
          {isAdmin && (
            <Grid
              size={{
                xs: 12,
                md: 1,
              }}>
              <Button variant="contained" size="small" onClick={showUserModal}>
                <Add />
                New
              </Button>
            </Grid>
          )}
        </Grid>
        {users.length > 0
          ? users.map((user) => <UserRow key={user.uid} user={user} />)
          : 'No other users found.'}
      </MainCard>
    );
  };

  return (
    <Grid size={12}>
      <Typography
        variant="h1"
        sx={{
          textAlign: 'center',
        }}>
        Partner Directory
      </Typography>
      <Box
        sx={{
          m: 4,
        }}
      />
      {unifiedPartners.length > 0 ? (
        employeeDefinitions.map((definition) => (
          <div key={definition.status}>
            <Typography
              variant="h2"
              sx={{
                pl: 6,
              }}>
              {definition.title}
            </Typography>
            <Box
              sx={{
                m: 4,
              }}
            />
            <PartnersCard
              key={definition.status}
              partners={unifiedPartners.filter(
                (partner) => partner.status === definition.status
              )}
            />
            <Box
              sx={{
                m: 6,
              }}
            />
          </div>
        ))
      ) : isLoadingPartners ? (
        <Loading />
      ) : (
        'No partners found.'
      )}
      {unmatchedUsers.length > 0 ? (
        <div key="other">
          <Typography
            variant="h2"
            sx={{
              pl: 6,
            }}>
            Other Users
          </Typography>
          <Box
            sx={{
              m: 4,
            }}
          />
          <UsersCard
            users={unmatchedUsers.sort((a, b) =>
              a.displayName.localeCompare(b.displayName)
            )}
          />
          <Box
            sx={{
              m: 6,
            }}
          />
        </div>
      ) : isLoadingUsers ? (
        <Loading />
      ) : (
        'No other users found.'
      )}
      {unmatchedPartners.length > 0 && (
        <div>
          <p>
            The following partners appear to not have an account. Please contact
            support.
          </p>
          <ul>
            {unmatchedPartners.map((partner) => (
              <li>{`${partner.initials} (${partner.id})`}</li>
            ))}
          </ul>
          <Box
            sx={{
              m: 6,
            }}
          />
        </div>
      )}
      <Box
        sx={{
          m: 4,
        }}
      />
    </Grid>
  );
};
